import api from '../services/api';
import { getSiteId } from '../siteId/siteId';
import { STOREFRONT_CHECKOUT_URL } from '../config/urls';
import createSerialQueue from '../lib/serialQueue';

const PROJECTS_V2_URL = '/projects/v2';

const projectService = {
    getAll,
    search,
    getCart,
    getSaved,
    getById,
    updateProject,
    setPromoCode,
    setLoyaltyCode,
    setShippingService,
    removeShippingService,
    addItems,
    duplicateItem,
    removeItem,
    updateItem,
    moveItemToSaved,
    moveItemToCart,
    getShipping,
    getItemTemplatePreview,
    requestRequote,
    decline,
    createProjectFromOrderItemIds,
    getCheckoutUrlFromProjectId,
    patchProject,
    getShipments,
    getCreditCard,
    updateCreditCard,
    order,
};

export default projectService;

function getAll (params = {}) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}`,
        method: 'GET',
        params,
    };

    return api(requestConfig);
}

function search (keyword) {
    return getAll({ keyword });
}

function getCart () {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/cart`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

function getSaved () {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/saved`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
        resolves: [{
            status: 400,
            data: {},
        }, {
            status: 417,
            data: {},
        }],
    };

    return api(requestConfig);
}

function getById (projectId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}`,
        method: 'GET',
    };

    return api(requestConfig);
}

function updateProject (project) {
    return patchProject(project.id, project);
}

function setPromoCode (projectId, code) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/promo?code=${code}`,
        method: 'POST',
    };

    return api(requestConfig);
}

function setLoyaltyCode (projectId, code) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/loyalty/coupon?code=${code}`,
        method: 'POST',
    };

    return api(requestConfig);
}

function setShippingService (projectId, shippingServiceId, custom) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/shipping/services/${shippingServiceId}?custom=${custom}`,
        method: 'POST',
    };

    return api(requestConfig);
}

function removeShippingService (projectId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/shipping/services`,
        method: 'DELETE',
    };

    return api(requestConfig);
}

function addItems (items) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/cart/addItems`,
        method: 'PUT',
        data: {
            items,
        },
    };

    return api(requestConfig);
}

function duplicateItem (projectId, itemId, changes) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/item/${itemId}/duplicate`,
        method: 'POST',
        data: changes,
    };

    return api(requestConfig);
}

function removeItem (projectId, itemId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/item/${itemId}`,
        method: 'DELETE',
    };

    return api(requestConfig);
}

function updateItem (projectId, item) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/item/${item.id}`,
        method: 'PATCH',
        data: item,
    };

    return api(requestConfig);
}

function moveItemToSaved (projectId, itemId, options = {}) {
    const params = {
        siteId: getSiteId(),
        includeCompanions: options.includeCompanions,
    };

    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/item/${itemId}/save`,
        method: 'POST',
        params,
    };

    return api(requestConfig);
}

function moveItemToCart (projectId, itemId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/item/${itemId}/cart`,
        method: 'POST',
        params: {
            siteId: getSiteId(),
        }
    };

    return api(requestConfig);
}

function getItemTemplatePreview (projectId, itemId, paramArgs) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/item/${itemId}/personalization/previews`,
        method: 'GET',
        params: Object.assign({}, paramArgs),
    };

    return api(requestConfig);
}

function getShipping (projectId, countryId, stateId, postalCode) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/shipping/services`,
        method: 'GET',
        params: {
            countryId,
            stateId,
            postalCode,
        },
    };

    return api(requestConfig);
}

function requestRequote (projectId, notes) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/requestRequote`,
        method: 'POST',
        data: {
            notes,
        },
    };

    return api(requestConfig);
}

function decline (projectId, notes) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/declineQuote`,
        method: 'POST',
        data: {
            notes,
        },
    };

    return api(requestConfig);
}

function createProjectFromOrderItemIds (projectId, orderItemIds) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/prepareQuote`,
        method: 'POST',
        data: {
            orderItemIds,
        },
    };

    return api(requestConfig);
}

function getCheckoutUrlFromProjectId (projectId) {
    return `${STOREFRONT_CHECKOUT_URL}${projectId}`;
}

/*
 * TTI-20060: concurrent PATCHes to the same project raced its optimistic-lock `updated_on` column and
 * threw StaleStateException, which surfaced to shoppers as "Error Occurred. Contact support". The
 * checkout shipping step fires two saves in quick succession by design, and each one triggers live
 * carrier rating, so overlap is easy to hit. Every PATCH for a given project is therefore queued
 * behind the one before it. The queue is keyed by project id, so unrelated projects still run
 * concurrently.
 */

const enqueueProjectPatch = createSerialQueue();

/**
 * Send a JSON merge patch to a project. This is the raw pass-through used by checkout, which patches
 * many more fields than {@link Project.toDB Project.toDB} covers.
 */

function patchProject (projectId, patch) {
    return enqueueProjectPatch(projectId, () => api({
        url: `${PROJECTS_V2_URL}/${projectId}`,
        method: 'PATCH',
        data: patch,
    }));
}

function getShipments (projectId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/orderShipments`,
        method: 'GET',
    };

    return api(requestConfig);
}

function getCreditCard (projectId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/creditCard`,
        method: 'GET',
        // No card set on the project is an expected state, not an error.
        resolves: [{
            status: 404,
            data: null,
        }],
    };

    return api(requestConfig);
}

/*
 * Stores a payment token on the project. This authorizes nothing - money moves only at order().
 *
 * The project's checkoutPaymentTypeId must already be CREDIT_CARD or PAYPAL when this is called.
 * ProjectV2Provider#updatePaymentInfo reads the payment type off the project first and skips the card
 * entirely for any other type, so patching the type afterwards silently discards the card (a 200 with
 * nothing stored), and calling this while the type is still null fails with a 500.
 */

function updateCreditCard (projectId, paymentInfo) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/creditCard`,
        method: 'PUT',
        data: paymentInfo,
    };

    return api(requestConfig);
}

function order (projectId) {
    const requestConfig = {
        url: `${PROJECTS_V2_URL}/${projectId}/order`,
        method: 'POST',
    };

    return api(requestConfig);
}
